import { useEffect, useRef, useState } from 'react';
import { useInventory } from '../../contexts/InventoryContext';
import { COLUMNS, COLUMN_ORDER, DEFAULT_VISIBLE_COLUMNS } from '../../contexts/inventoryColumns';

/**
 * ColumnPicker — show/hide table columns from the inventory toolbar.
 *
 * Click "Columns ▾" to open. Each non-locked column has a checkbox.
 * The Name column is locked (rendered greyed-out / disabled).
 *
 * Search input filters the list as you type — keeps the panel useful
 * once more than ~10 columns exist.
 */
export default function ColumnPicker() {
  const { dispatch, ...state } = useInventory();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const visible = new Set(state.visibleColumns || []);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQuery('');
  }, [open]);

  const visibleNonLocked = COLUMN_ORDER.filter(
    id => COLUMNS[id] && !COLUMNS[id].locked && visible.has(id)
  ).length;
  const totalNonLocked = COLUMN_ORDER.filter(id => COLUMNS[id] && !COLUMNS[id].locked).length;

  const filtered = COLUMN_ORDER.filter((id) => {
    const col = COLUMNS[id];
    if (!col) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return col.header.toLowerCase().includes(q)
        || (col.description || '').toLowerCase().includes(q);
  });

  return (
    <div className="inv-col-picker" ref={wrapperRef}>
      <button
        type="button"
        className={`btn btn-sm inv-col-picker-btn ${open ? 'inv-col-picker-btn--active' : ''}`}
        onClick={() => setOpen(!open)}
        title="Show or hide columns"
      >
        ☷ Columns
        <span className="inv-col-picker-count">{visibleNonLocked}/{totalNonLocked}</span>
        {open ? ' ▼' : ' ▶'}
      </button>
      {open && (
        <div className="inv-col-picker-menu" role="dialog" aria-label="Show or hide columns">
          <input
            ref={inputRef}
            type="text"
            className="inv-input inv-col-picker-search"
            placeholder="Find column…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="inv-col-picker-list">
            {filtered.length === 0 && (
              <div className="inv-col-picker-empty">No matching columns.</div>
            )}
            {filtered.map((id) => {
              const col = COLUMNS[id];
              const isVisible = visible.has(id);
              const locked = !!col.locked;
              return (
                <label
                  key={id}
                  className={`inv-col-picker-item ${locked ? 'inv-col-picker-item--locked' : ''}`}
                  title={locked ? 'Always visible' : col.description}
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    disabled={locked}
                    onChange={() => dispatch({ type: 'TOGGLE_COLUMN', id })}
                  />
                  <span className="inv-col-picker-item-name">{col.header}</span>
                  {col.description && (
                    <span className="inv-col-picker-item-desc">{col.description}</span>
                  )}
                  {locked && <span className="inv-col-picker-item-lock">locked</span>}
                </label>
              );
            })}
          </div>
          <div className="inv-col-picker-footer">
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => dispatch({ type: 'RESET_COLUMNS' })}
              title="Reset to default columns"
              disabled={arraysEqual(state.visibleColumns, DEFAULT_VISIBLE_COLUMNS)}
            >
              Reset to default
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => dispatch({ type: 'RESET_COLUMN_WIDTHS' })}
              title="Put every column back to its default width (drag a header's edge to change one; double-click the edge to reset it)"
              disabled={!state.columnWidths || Object.keys(state.columnWidths).length === 0}
            >
              Reset widths
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
